import React, { useEffect, useRef, useState } from 'react';
import { paymentsApi } from '../../utils/paymentsStore';
import { Icon } from '../ui';

const MAX_PDF_BYTES = 4 * 1024 * 1024;

// Reads one claim or remittance PDF through the backend and hands the result
// to the claim form. The file is read once in the browser, sent for
// extraction, and dropped; only the fields the admin confirms are stored.
export default function ClaimPdfDrawer({ onClose, onExtracted, onOpenExisting, showToast }) {
  const fileRef = useRef(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // only set when the claim already exists
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('');
  const timerRef = useRef(null);

  // One request with no progress events, so the bar is timed: it climbs
  // quickly at first, eases toward 92 percent, and completes when the
  // response arrives. The stage text tracks what the backend is doing.
  const startProgress = () => {
    const t0 = Date.now();
    setProgress(2);
    setStage('Uploading the file');
    timerRef.current = setInterval(() => {
      const t = Date.now() - t0;
      setProgress(Math.min(92, 100 * (1 - Math.exp(-t / 5000))));
      setStage(t < 900 ? 'Uploading the file' : t < 3000 ? 'Reading the pages' : 'Pulling out the claim details');
    }, 200);
  };
  const stopProgress = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    setProgress(100);
  };
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const readFile = (file) => {
    setError('');
    if (!file) return;
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
    if (!isPdf) { setError('Upload a PDF file'); return; }
    if (file.size > MAX_PDF_BYTES) { setError('PDF must be 4 MB or smaller'); return; }
    setBusy(true);
    startProgress();
    const reader = new FileReader();
    reader.onerror = () => { stopProgress(); setBusy(false); setError('Could not read the file'); };
    reader.onload = async () => {
      let fileData = String(reader.result || '').split(',')[1] || '';
      try {
        const r = await paymentsApi.extractClaimPdf(fileData, file.name);
        fileData = '';
        const found = Object.keys(r.pdf.fields).length;
        if (r.pdf.exists) {
          setResult(r);
        } else {
          showToast(`Read ${found} field${found === 1 ? '' : 's'} from ${file.name}; confirm the highlighted ones`);
          onExtracted(r);
        }
      } catch (e) {
        setError(e.message || 'Could not read the PDF');
      } finally {
        fileData = '';
        stopProgress();
        setBusy(false);
        if (fileRef.current) fileRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const onDrop = (e) => { e.preventDefault(); setOver(false); readFile(e.dataTransfer.files?.[0]); };

  return (
    <>
      <div className="ops-drawer__backdrop" onClick={onClose} />
      <aside className="ops-drawer ops-drawer--narrow" role="dialog" aria-modal="true" aria-label="Upload claim PDF">
        <div className="ops-drawer__head">
          <h2>Upload claim PDF</h2>
          <button type="button" className="ops-close" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
        </div>
        <div className="ops-drawer__body">
          {error && <div className="ops-error">{error}</div>}
          {result ? (
            <>
              <div className="ops-note ops-note--review">
                <b>Claim # {result.defaults.claimNumber} is already in the payment log.</b> You can update the existing claim with what this PDF shows, or start a new one from it anyway.
              </div>
              <div className="ops-actions" style={{ flexWrap: 'wrap' }}>
                <button type="button" className="ops-btn ops-btn--primary" onClick={() => onOpenExisting(result)}>Update existing claim</button>
                <button type="button" className="ops-btn" onClick={() => onExtracted(result)}>Start a new claim anyway</button>
              </div>
            </>
          ) : (
            <>
              <div className={`ops-dropzone${over ? ' is-over' : ''}${busy ? ' is-busy' : ''}`}
                onDragOver={e => { e.preventDefault(); if (!busy) setOver(true); }} onDragLeave={() => setOver(false)} onDrop={busy ? e => e.preventDefault() : onDrop}
                onClick={() => !busy && fileRef.current?.click()} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && !busy && fileRef.current?.click()}
                aria-busy={busy}>
                <Icon name={busy ? 'clock' : 'file'} size={28} />
                {busy ? (
                  <>
                    <div><b>Reading PDF</b> {stage}</div>
                    <div className="ops-progress" role="progressbar" aria-label="Reading PDF" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)}>
                      <div className="ops-progress__bar" style={{ width: `${progress}%` }} />
                    </div>
                    <div className="ops-cell-sub">Usually a few seconds, up to 30.</div>
                  </>
                ) : (
                  <>
                    <div><b>Drop the claim PDF here</b> or click to browse</div>
                    <div className="ops-cell-sub">One claim or remittance per file, PDF only, 4 MB or smaller.</div>
                  </>
                )}
                <input ref={fileRef} type="file" accept=".pdf,application/pdf" hidden onChange={e => readFile(e.target.files?.[0])} />
              </div>
              <p className="ops-help">The PDF is read once to fill in the claim form. It is not saved anywhere, and only the fields you confirm are stored in the payment log. Every insurance company lays its documents out differently, so check the highlighted fields before adding the claim.</p>
            </>
          )}
        </div>
        <div className="ops-drawer__foot">
          <span />
          <span className="ops-actions"><button type="button" className="ops-btn" onClick={onClose}>Cancel</button></span>
        </div>
      </aside>
    </>
  );
}
